//================================================================
//  Component: Lookup Live Fields
//================================================================

//  Purpose: This is an input field that allows you to search any Firestore collection with the 'searcharray' field

//  Properties:
//    - collectionid = {string, Firestore collection id}
//    - defaultValue = {string, onload value for the input}
//    - placeholder = {string, placeholder string}
//    - errors = {defined value, this determines if this field is in its error state}
//    - disabled = {boolen, enable or disable the field}
//    - handleBlur = {call back function, triggers the save event to the Firestore Document}
//    - fieldStatus = {useState, LiveField component status}
//    - setFieldStatus = {useState, LiveField component status}
//    - dataFieldPrimary = {string, field key which is saved to the Firestore document and SHOW in the drop down}
//    - dataFieldSecondary = <Optional> {string, field key which is saved to the Firestore document and SHOW in the drop down}
//    - dataFieldTertiary = <Optional> {string, field key which is saved to the Firestore document and IS NOT shown in the dropdown. Good for a documentid, etc}
//    - createNewHTML = <Optional> {HTML, generic html attached to the bottom of the search results}
//    - required = {boolean, if true, the field is required}

//  Example:

//    <LookUpLiveFields
//      collectionid={'users'}
//      defaultValue={'mark.bennett@lendlease.com'}
//      placeholder={'Search user by email address'}
//      dataFieldPrimary={'emailaddress'}
//      dataFieldSecondary={'givenname'}
//      dataFieldTertiary={'surname'}
//      createNewHTML={<span className='font-normal text-[#A0A0A0]'>Can't find what you're looking for? Click <a href='http://console.digitalservices.lendlease.com/inventory?view=vendors' target='_blank' rel='noopener noreferrer'>here</a>.</span>}
//      required={false}
//    ></LookUpLiveFields>    

//================================================================


//Libraries
import React, { useReducer, useEffect } from 'react';
import { Link } from 'react-router-dom';

//Contexts

//Components

//Functions
import QueryDocument from '../../../Library/QueryDocument';

//Images
import successIcon from '../../Images/Icon_Save_Teal.svg';
import pendingIcon from '../../Images/Icon_LoadingFile_Grey.gif';
import errorIcon from '../../Images/Icon_Error_Red.svg';


export default function LookUpLiveFields({
    collectionid,
    defaultValue,
    placeholder,
    disabled,
    handleBlur,
    fieldStatus,
    setFieldStatus,
    dataFieldPrimary,
    dataFieldSecondary,
    dataFieldTertiary,
    createNewHTML,
    required,
}) {

    // Check if the app is in development environment
    const isDevelopment = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285';

    // Check if the app is in uat environment
    const isUAT = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396';

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'userInput': '',
            'queryMessage': '',
            'searchResults': []
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to handle search
    const handleSearch = (value) => {

        setSearchData({
            'searchResults': [],
            'userInput': value,
        });

        // Validate input
        if (value?.length === 0) {

            return setSearchData({ 'queryMessage': 'Please enter a value.' });

        }

        if (value?.length <= 2) {

            return setSearchData({ 'queryMessage': 'Search requires more than two characters.' });

        }

        const query = value?.toLowerCase();

        // Search by characters in email
        QueryDocument(collectionid, [
            ['searcharray', 'array-contains-any', [query]],
        ])
            .then((docs) => {

                setSearchData({
                    'searchResults': docs,
                    'queryMessage': `Showing ${docs?.length} result(s)`
                });

            })
            .catch((errors) => {

                console.log(errors);
                setSearchData({ 'queryMessage': 'Oops something went wrong!' });

            });

    }

    // Retrieves the nested value from an object based on the given dot-separated path.
    function getNestedValue(obj, path) {

        if (!obj || typeof path !== "string") return undefined;

        return path.split('.').reduce((acc, key) => acc && acc[key], obj);
    }

    // Retrive the field name for for nested values
    function getLastFieldName(path) {
        if (typeof path !== "string") return undefined;

        const keys = path.split('.');
        return keys.length > 0 ? keys[keys.length - 1] : undefined;
    }

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Set the default value onload
    useEffect(() => {

        if (defaultValue === undefined) return;

        setSearchData({ 'userInput': defaultValue })

    }, [defaultValue])

    // onChange
    // 1. Check if the field is required
    // 2. Check if the field is empty
    // 3. Set the field status
    useEffect(() => {

        if (required === false) return;
        if (required === undefined) return;

        if (searchData?.userInput?.length === 0) {

            return setFieldStatus('error');

        }

        setFieldStatus('onload');

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData.userInput])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // pending
    if (fieldStatus === 'pending') {

        return (
            <div className='flex flex-row gap-2'>

                {/* ========= Input ========= */}
                <div className='flex flex-col relative'>

                    {/* ------------------------------------------------------ */}
                    {/*  Users Search Bar                                      */}
                    {/* ------------------------------------------------------ */}

                    {/* Search Bar */}
                    <div className='flex flex-row items-center'>

                        {/* Search Input Field */}
                        <input
                            className='w-[450px] h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-[5px]'
                            type='text'
                            placeholder={placeholder}
                            value={searchData.userInput}
                            disabled={true}
                        ></input>

                    </div>

                </div>

                {/* ========= Pending Icon ========= */}
                <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>

            </div>
        )

    }

    // onload, success & error
    else {

        return (

            <div className='flex flex-col gap-2'>

                <div className='flex flex-row relative gap-2'>

                    {/* ------------------------------------------------------ */}
                    {/*  Users Search Bar                                      */}
                    {/* ------------------------------------------------------ */}

                    {/* Search Bar */}
                    <div className='flex flex-row items-center'>

                        {/* Search Input Field */}
                        <input
                            className={
                                fieldStatus === 'error' ?
                                    'w-[450px] h-[42px] px-[15px] py-[10px] bg-[white] border-[1px] border-solid border-[#DE0000] rounded-[5px] disabled:text-[#2a2a2a] disabled:bg-[#f8f8f8]'
                                    :
                                    searchData?.searchResults?.length > 0 ?
                                        'w-[450px] h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-tl-[5px] rounded-br-none rounded-tr-[5px] rounded-bl-none disabled:text-[#2a2a2a] disabled:bg-[#f8f8f8]'
                                        :
                                        'w-[450px] h-[42px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-[5px] disabled:text-[#2a2a2a] disabled:bg-[#f8f8f8]'
                            }
                            type='text'
                            placeholder={placeholder}
                            onChange={(e) => handleSearch(e.target.value)}
                            onPaste={(e) => handleSearch(e.clipboardData.getData('Text'))}
                            autoComplete='off'
                            value={searchData.userInput}
                            disabled={disabled}
                        ></input>

                    </div>

                    {/* Search Results */}
                    {
                        (
                            <div className='absolute w-[450px]  z-10 top-11 max-h-[150px] overflow-y-auto bg-white shadow-md rounded-tl-none rounded-br-[5px] rounded-tr-none rounded-bl-[5px]'>

                                {/* No Results */}
                                {
                                    (searchData?.searchResults?.length === 0 && defaultValue !== searchData?.userInput && searchData?.userInput !== '') &&
                                    <div className='font-medium text-[14px] px-3 py-[12px]'>
                                        No results found.
                                    </div>
                                }

                                {/* Results */}
                                {
                                    ((searchData?.searchResults?.length > 0 || defaultValue !== searchData.userInput) && searchData.userInput !== '') &&
                                    searchData?.searchResults.map((document, index) => (
                                        <div
                                            key={index}
                                            className='flex flex-row justify-between px-4 py-2 cursor-pointer rounded-sm hover:bg-[#F0F0F0]'
                                            onClick={() => {

                                                const resultsObj = {
                                                    [dataFieldPrimary]: document[dataFieldPrimary],
                                                }

                                                if (dataFieldSecondary) {

                                                    resultsObj[getLastFieldName(dataFieldSecondary)] = getNestedValue(document, dataFieldSecondary);

                                                }

                                                if (dataFieldTertiary) {

                                                    resultsObj[getLastFieldName(dataFieldTertiary)] = getNestedValue(document, dataFieldTertiary);

                                                }

                                                // Update the search results
                                                setSearchData({
                                                    'searchResults': [],
                                                    'userInput': document[dataFieldPrimary],
                                                });

                                                handleBlur(resultsObj, true);

                                            }}
                                        >

                                            {/* Detail */}
                                            <div className='flex flex-col'>
                                                <p className='m-0 font-medium text-[14px]'>{document?.[dataFieldPrimary]}</p>
                                                <p className='m-0 text-[14px]'>{getNestedValue(document, dataFieldSecondary)}</p>
                                            </div>

                                        </div>
                                    ))
                                }

                                {/* Total */}
                                {
                                    ((searchData?.searchResults?.length > 0 || defaultValue !== searchData.userInput) && searchData.userInput !== '') &&
                                    <div className='flex flex-col text-sm font-medium px-4 py-2 border-t border-t-[#D8D8D8]' hidden={searchData.queryMessage.length === 0}>
                                        {searchData.queryMessage}
                                        {createNewHTML}
                                    </div>
                                }

                            </div>
                        )
                    }

                    {/* ========= Onload, Success or Error Icon ========= */}
                    {
                        fieldStatus === 'onload' ?
                            null

                            : fieldStatus === 'success' ?
                                <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>

                                :

                                <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
                    }


                </div>
                {/* Custom error message */}
                {


                    fieldStatus === 'error' && required === true ?
                        <label className='text-[#DE0000]'>This field is required</label>

                        : fieldStatus === 'error' && required === false ?

                            <label className='text-[#DE0000]'>Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.</label>


                            : null
                }

            </div>

        )

    }


    //------------------------------------------------------
}